import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCheck, X } from 'lucide-react';
import { useNotificationStore } from '../stores/notificationStore';
import { cn } from '../lib/utils';

const PRIORITY_COLORS = {
  urgent: 'border-l-red-500',
  high:   'border-l-orange-400',
  normal: 'border-l-blue-400',
  low:    'border-l-gray-300',
};

export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate    = useNavigate();
  const isRtl       = i18n.dir() === 'rtl';

  const [open, setOpen] = useState(false);
  const dropdownRef     = useRef(null);

  const { notifications, unreadCount, isLoading, fetchNotifications, fetchUnreadCount, markRead, markAllRead } =
    useNotificationStore();

  // Poll unread count every 60 s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60_000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch list when dropdown opens
  useEffect(() => {
    if (open) fetchNotifications({ limit: 10 });
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) markRead(notification.id);
    if (notification.action_url) navigate(notification.action_url);
    setOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label={t('notifications.title')}
      >
        <Bell size={20} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute top-1 end-1 min-w-[16px] h-4 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center px-0.5">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className={cn(
            'absolute top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden',
            isRtl ? 'left-0' : 'right-0'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-semibold text-gray-800 text-sm">{t('notifications.title')}</span>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                >
                  <CheckCheck size={14} />
                  {t('notifications.markAllRead')}
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-gray-100">
                <X size={14} className="text-gray-400" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {isLoading ? (
              <div className="py-8 text-center text-sm text-gray-400">{t('common.loading')}...</div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">{t('notifications.noNotifications')}</div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={cn(
                    'w-full text-start px-4 py-3 hover:bg-gray-50 transition-colors border-l-2',
                    PRIORITY_COLORS[n.priority] || PRIORITY_COLORS.normal,
                    !n.is_read && 'bg-blue-50/50'
                  )}
                >
                  <p className={cn('text-sm font-medium text-gray-800', !n.is_read && 'font-semibold')}>
                    {i18n.language === 'ar' ? n.title_ar : (n.title_en || n.title_ar)}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                    {i18n.language === 'ar' ? n.message_ar : (n.message_en || n.message_ar)}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {new Date(n.created_at).toLocaleString(i18n.language === 'ar' ? 'ar-JO' : 'en-GB')}
                  </p>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-100">
            <button
              onClick={() => { navigate('/notifications'); setOpen(false); }}
              className="w-full text-center text-xs text-primary-600 hover:text-primary-700 font-medium py-1"
            >
              {t('notifications.viewAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
